import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight, History } from 'lucide-react';
import QuestTimeline, { type PastQuest } from './QuestTimeline';
import { fetchPastWinners } from '@/lib/api';

/**
 * Home-page teaser: 3 most-recent winners as a compact timeline.
 * Mobile cards skip the YouTube embed (compactOnMobile) so the
 * landing page stays light. CTA at the bottom links to /past.
 *
 * The section auto-hides when the DB has no published past quests yet.
 */
export function RecentTimeline() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<PastQuest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchPastWinners({ limit: 3, lang: i18n.language.slice(0, 2) })
      .then((res) => {
        setItems(
          res
            .filter((w) => w.video_published_url)
            .slice(0, 3)
            .map((w) => ({
              id: w.quest.id,
              week_iso: w.week_iso,
              title: w.quest.title,
              author: w.quest.author_username,
              total_cents: w.total_paid_cents,
              video_url: w.video_published_url ?? '',
              date: w.decided_at,
            })),
        );
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [i18n.language]);

  // Render nothing while loading or when no real data exists yet.
  if (loading || items.length === 0) return null;

  return (
    <section className="section">
      <div className="container-narrow">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.45 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-5 pill-pixel
                          bg-violet-500/10 border-violet-500/30 text-violet-300">
            <History className="w-4 h-4" />
            {t('recent_timeline.tag', { defaultValue: 'Cronologia' })}
          </div>
          <h2 className="font-pixel text-4xl sm:text-5xl font-bold tracking-tight">
            {t('recent_timeline.title', { defaultValue: 'Le quest realizzate' })}
          </h2>
          <p className="mt-4 text-kquest-300 text-lg max-w-2xl mx-auto">
            {t('recent_timeline.subtitle', {
              defaultValue: 'Ogni settimana scelgo la quest più votata e la realizzo. Ecco le ultime.',
            })}
          </p>
        </motion.div>

        <QuestTimeline pastQuests={items} limit={3} compactOnMobile />

        <div className="mt-12 text-center">
          <Link
            to="/past"
            className="inline-flex items-center gap-2 btn-purple !px-8 !py-3 group"
          >
            {t('recent_timeline.cta', { defaultValue: 'Vedi tutte le quest passate' })}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}
